import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { Button, Card, Tag, Typography } from "antd";
import { CalendarListIcon } from "../../assets/CustomIcon";

import { PROMOTION_TAGS } from "../../utils/constants";
import ImageCardDisplay from "../common/ImageCardDisplay";

const { Text } = Typography;

const EventCard = ({ event, layout = "default" }) => {
  const navigate = useNavigate();
  
  // Format date and time
  const formatDate = (dateTimeString) => {
    if (!dateTimeString) return "";
    return dayjs(dateTimeString).format("DD MMM");
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    return dayjs(dateTimeString).format("hh:mm A");
  };
  
  const startDate = formatDate(event?.event_start_date_time);
  const startTime = formatTime(event?.event_start_date_time);
  const eventPrice = event?.event_type_id === 1 ? "Free" : `from ₹${Math.min(...(event?.tickets?.map(t => t.price) || [0]))}`;
  const eventCategory = event?.category?.name || event?.sub_category?.name || "Event";
  const eventSlug = event?.slug;

  const goToEventDetails = () => {
    if (eventSlug) navigate(`/event-details/${eventSlug}`);
  };

  const handleActionClick = (event) => {
    event.stopPropagation();
    goToEventDetails();
  };

  const handleCardKeyDown = (event) => {
    if (!eventSlug) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToEventDetails();
    }
  };

  const clickableClass = eventSlug ? "eventCardClickable" : "";

  if (layout === "featured") {
    return (
      <Card
        hoverable
        className={`eventCardFeatured ${clickableClass}`}
        onClick={eventSlug ? goToEventDetails : undefined}
        onKeyDown={handleCardKeyDown}
        tabIndex={eventSlug ? 0 : undefined}
        role={eventSlug ? "button" : undefined}
      >
        <div className="eventCardFeaturedImgWrap">
          <ImageCardDisplay cardElement={event} isCard={true} customClass="eventCardFeaturedImg" />
        </div>
        <div className="eventCardFeaturedBody">
          <Tag className="eventTag blue">{eventCategory}</Tag>
          <h3 className="eventCardVTitle">{event?.event_name}</h3>
          <Text className="eventCardVLocation">{event?.venue?.name}</Text>
          <div className="eventCardVMeta">
            <span className="event-icon">
              <CalendarListIcon />
            </span>
            <Text className="eventCardVDate">{startDate}</Text>
            <Text className="eventCardVTime">{startTime}</Text>
          </div>
          <div className="eventCardFeaturedFooter">
            <Text className="eventCardVPrice">{eventPrice}</Text>
            <Button className="eventActionBtn" onClick={handleActionClick} disabled={!eventSlug}>
              View
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (layout === "upcoming") {
    return (
      <article
        className={`eventCardUpcoming ${clickableClass}`}
        onClick={eventSlug ? goToEventDetails : undefined}
        onKeyDown={handleCardKeyDown}
        tabIndex={eventSlug ? 0 : undefined}
        role={eventSlug ? "button" : undefined}
      >
        <div className="eventCardUpcomingImageWrap">
          <ImageCardDisplay cardElement={event} isCard={true} customClass="eventCardUpcomingImage" />
        </div>
        <div className="eventCardUpcomingContent">
          <div className="eventCardUpcomingTopRow">
            <Tag className="eventTag red">{eventCategory}</Tag>
            <span className="eventDatePill">
              {startDate} • {startTime}
            </span>
          </div>
          <h3 className="eventCardVTitle">{event?.event_name}</h3>
          <Text className="eventCardVLocation">{event?.venue?.name}</Text>
          <div className="eventCardUpcomingBottomRow">
            <Text className="eventCardVPrice">{eventPrice}</Text>
            <Button className="eventActionBtn" onClick={handleActionClick} disabled={!eventSlug}>
              Book Now
            </Button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <Card hoverable className="eventCardV">
      <div className="eventCardVImgWrap">
        <ImageCardDisplay cardElement={event} isCard={true} customClass="eventCardVImg" />
      </div>
      <div className="eventCardVContent">
        <div className="eventTags">
          {event?.event_promo_tags && event?.event_promo_tags.split(',').map((badge, index) => (
            <Tag key={index} className={`eventTag ${PROMOTION_TAGS.find(tag => tag.value === badge)?.color}`}>
              {PROMOTION_TAGS.find(tag => tag.value === badge)?.label}
            </Tag>
          ))}
        </div>
        <h3 className="eventCardVTitle">{event?.event_name}</h3>
        <div className="eventCardVMeta">
          <span className="event-icon">
            <CalendarListIcon />
          </span>
          <Text className="eventCardVDate">{startDate}</Text>
          <Text className="eventCardVTime">{startTime}</Text>
        </div>
        <Text className="eventCardVLocation">{event?.venue?.name}</Text>
        <Text className="eventCardVPrice">{eventPrice}</Text>
      </div>
    </Card>
  );
};

export default EventCard;
